import React, { FC } from "react";
import { useLocation, useParams, useSearchParams } from "react-router-dom";

import { Translate } from "@/app/application/Translate";
import { KpiParameterStatistics } from "@/features/training/pages/Archive/panel-right/KpiParameterStatistics";
import { DrillSession } from "@/features/training/pages/drill-tab/panel-right/drill-session/DrillSession";
import { CourseSession } from "@/features/training/pages/training-tab/panel-right/training-session/CourseSession";
import { FallBackErrorMessage } from "@/features/training/ui";
import FolderOpenOutlinedIcon from "@mui/icons-material/FolderOpenOutlined";
import { Box, Typography } from "@mui/material";
import * as Sentry from "@sentry/react";

interface ArchiveLocationState {
	courseId?: number;
	drillId?: number;
}

const isArchiveLocationState = (
	value: unknown,
): value is ArchiveLocationState => {
	if (typeof value !== "object" || value === null) {
		return false;
	}

	const state = value as Record<string, unknown>;
	const isCourseIdValid =
		state.courseId === undefined || typeof state.courseId === "number";
	const isDrillIdValid =
		state.drillId === undefined || typeof state.drillId === "number";

	return isCourseIdValid && isDrillIdValid;
};

const ArchiveEmptyState: FC = () => (
	<Box
		sx={{
			flex: 1,
			minHeight: 0,
			display: "flex",
			flexDirection: "column",
			alignItems: "center",
			justifyContent: "center",
			px: 3,
			textAlign: "center",
		}}
	>
		<Box
			sx={{
				p: 4,
				borderRadius: 2,
				border: "1px dashed",
				borderColor: "divider",
				bgcolor: "action.hover",
				maxWidth: 360,
			}}
		>
			<FolderOpenOutlinedIcon
				sx={{ fontSize: 56, color: "text.disabled", mb: 2 }}
			/>
			<Typography
				variant="h6"
				fontWeight={700}
				color="text.secondary"
				gutterBottom
			>
				<Translate String="Training.Archive.EmptyState.Title">
					Select a session
				</Translate>
			</Typography>
			<Typography variant="body2" color="text.secondary">
				<Translate String="Training.Archive.EmptyState.Description">
					Choose an archived session or KPI from the list to view its details
					here.
				</Translate>
			</Typography>
		</Box>
	</Box>
);

export const ArchivePanelRight: FC = () => {
	const { sessionId, kpiParameterId } = useParams();
	const location = useLocation();
	const routeState = isArchiveLocationState(location.state)
		? location.state
		: undefined;
	const courseId = routeState?.courseId;
	const drillId = routeState?.drillId;
	const [searchParams] = useSearchParams();
	const startDate = searchParams.get("StartDate");
	const endDate = searchParams.get("EndDate");

	const content =
		courseId && sessionId ? (
			<Sentry.ErrorBoundary fallback={<FallBackErrorMessage />}>
				<CourseSession sessionId={parseInt(sessionId)} courseId={courseId} />
			</Sentry.ErrorBoundary>
		) : drillId && sessionId ? (
			<Sentry.ErrorBoundary fallback={<FallBackErrorMessage />}>
				<DrillSession sessionId={parseInt(sessionId)} drillId={drillId} />
			</Sentry.ErrorBoundary>
		) : kpiParameterId ? (
			<Sentry.ErrorBoundary fallback={<FallBackErrorMessage />}>
				<KpiParameterStatistics
					Id={parseInt(kpiParameterId)}
					StartDate={startDate}
					EndDate={endDate}
				/>
			</Sentry.ErrorBoundary>
		) : (
			<ArchiveEmptyState />
		);

	return (
		<Box
			data-tour="training-archive-right"
			sx={{
				flex: 1,
				minHeight: 0,
				display: "flex",
				flexDirection: "column",
				overflow: "hidden",
			}}
		>
			{content}
		</Box>
	);
};
